import { sanityClient } from '@/sanity/lib/client'
import { urlFor } from '@/sanity/lib/image'
import { BLOG_POSTS_QUERY, LOCATIONS_QUERY, PAGE_QUERY, SHOP_CATEGORIES_QUERY } from '@/sanity/lib/queries'
import { PageBuilder } from '@/components/PageBuilder'
import { NextSeo } from 'next-seo';
import { useContext, useEffect } from 'react';
import StoreContext from '@/context/store';
import { getSiteSettings } from '@/lib/getSiteSettings';

export async function getServerSideProps({ params }) {
  const slug = Array.isArray(params.slug) ? params.slug.join("/") : params.slug;
  const siteSettings = await getSiteSettings();
  const shopCategories = await sanityClient.fetch(SHOP_CATEGORIES_QUERY);

  // Fetch locations and slug pages TODO clean this once locations are deprecated
  const locations = await sanityClient.fetch(LOCATIONS_QUERY);
  const slugPages = await sanityClient.fetch(`
    *[_type == "page" && slug.current match "weed-delivery/*"]{
      "city": slug.current
    }
  `);
  const fromLocations = locations.map(loc => loc.city?.toLowerCase()).filter(Boolean);

  const fromSlugs = slugPages.map(page => {
    const parts = page.city.split("/");
    return parts.length > 1 ? parts[1].replace(/-/g, " ").toLowerCase() : null;
  }).filter(Boolean);

  const cities = Array.from(
    new Set([...fromLocations, ...fromSlugs])
  ).filter((city) => city.toLowerCase() !== "test");
  // end fetch locations 

  const page = await sanityClient.fetch(PAGE_QUERY, {
    slug
  });

  const posts = await sanityClient.fetch(BLOG_POSTS_QUERY(0, 9));

  if (!page || !page.content) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      page,
      shopCategories,
      locations,
      cities,
      posts,
      siteSettings
    },
  }
}

export default function Page({ page, shopCategories, locations, cities, posts }) {

  const { stores, pickupStores, fetchPickupStores, fetchStores, deliveryZips, fetchDeliveryZips } = useContext(StoreContext);

  const metaTitle = page.metaTitle || page.title;
  const metaDescription = page.metaDescription || '';
  const seoImage = page.mainImage ? urlFor(page.mainImage).width(1200).height(630).url() : null;
  const canonicalUrl = `https://www.plpcsanjose.com/${page.slug?.current || ''}`;

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (stores) {
      fetchPickupStores();
    }
  }, [stores, fetchPickupStores]);

  useEffect(() => {
    if (stores) {
      fetchDeliveryZips();
    }
  }, [stores, fetchDeliveryZips]);

  if (!page?.content) return null;

  return (
    <>
      <NextSeo
        noindex={page.noindex === true}
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalUrl,
          type: 'website',
          images: seoImage
            ? [{ url: seoImage, width: 1200, height: 630, alt: metaTitle }]
            : [],
        }}
        twitter={{
          cardType: seoImage ? 'summary_large_image' : 'summary',
        }}
      />

      <PageBuilder
        content={page.content}
        shopCategories={shopCategories}
        locations={locations}
        cities={cities}
        stores={stores}
        deliveryZips={deliveryZips}
        posts={posts}
        pickupStores={pickupStores}
      />
    </>
  );
}
